import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { QueryType } from 'enums/QueryType';
import { environment } from "environments/environment";
import { QueryModel } from 'models/generated/Entities.GormCrm.Query';
import { DynamicQueryModel, SelectPropertyModel, AggregationEnum } from 'models/generated/Models.DynamicQuery';
import { SaveQueryRequestModel } from 'models/generated/Models.Query.SaveQueryRequest';
import { QueryPropertyModel, QueryPropertyType, QueryPropertyValueType } from 'models/generated/Models.QueryProperty';
import { Comparison, ExpressionFilterModel } from 'models/generated/Models.ExpressionFilter';
import { DynamicQueryValidationModel } from 'models/generated/Models.Query.DynamicQueryValidation';
import { RelationService } from 'services/relation.service';
import { firstValueFrom } from 'rxjs';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { TaskflowModel } from 'models/generated/Entities.GormCrm.Taskflow';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { OrderModel } from 'models/generated/Entities.GormCrm.Order';
import { ProductModel } from 'models/generated/Entities.GormCrm.Product';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { OrderCancellationModel } from 'models/generated/Entities.GormCrm.OrderCancellation';
import { OrderWarningModel } from 'models/generated/Entities.GormCrm.OrderWarning';
import { OrderLineModel } from 'models/generated/Entities.GormCrm.OrderLine';
import { OrderProductPlanDiscontinuationModel } from 'models/generated/Entities.GormCrm.OrderProductPlanDiscontinuation';
import { SettlementModel } from 'models/generated/Entities.GormCrm.Settlement';
import { TransferModel } from 'models/generated/Entities.GormCrm.Transfer';
import { ModelProperty } from 'models/Model';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { UserPipe } from 'pipes/user.pipe';
import { TeamPipe } from 'pipes/team.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';

export type QueryResults = TaskActionModel | TaskflowModel | CompanyModel | OrderModel | ProductModel | ProductPlanModel | OrderCancellationModel
	| OrderWarningModel | OrderLineModel | OrderProductPlanDiscontinuationModel | SettlementModel | TransferModel;

// Response of the Ai.Api query builder (POST api/ai/query-builder), see Ai.Api/Prompts/QueryBuilder/system-prompt.md.
// The reply is untyped JSON on the server, so these are written by hand instead of generated.
/** A filter value referencing another query in the same reply that hasn't been saved yet. */
export interface QueryReference {
	queryRef: string;
}
/** A sub-query sent or saved inline as a filter value. The server resolves the property definitions from their names. */
export interface InlineQuery {
	queryType: QueryType;
	filters: InlineQueryFilter[];
	selectProperties: SelectPropertyModel[];
}
export interface InlineQueryFilter {
	property: { name: string };
	operator: Comparison;
	value: FilterValue;
}
/** What a filter's value can hold: a plain value, a saved query's id, a reference to an unsaved query, or an inline sub-query. */
export type FilterValue = string | number | boolean | Date | null | undefined | QueryReference | InlineQuery;
export interface QueryBuilderNamedQuery {
	key: string;
	name: string;
	description: string;
	data: DynamicQueryModel;
}
export interface QueryBuilderReply {
	status: 'query' | 'clarification' | 'unsupported';
	message: string;
	queries: QueryBuilderNamedQuery[];
	mainQuery: string | null;
}
export interface QueryBuilderDryRun {
	key: string;
	validation?: DynamicQueryValidationModel;
}
export interface QueryBuilderResponse {
	reply: QueryBuilderReply;
	dryRuns: QueryBuilderDryRun[];
}
export interface QueryBuilderMessage {
	role: 'user' | 'assistant';
	content: string;
}

@Injectable()
export class QueryService {
	private baseUrl = `${environment.gatewayUrl}/api/queries`;

	constructor(private readonly http: HttpClient, private readonly datePipe: CustomDatePipe, private readonly currencyPipe: CurrencyPipe, private readonly translate: TranslatePipe, private readonly userPipe: UserPipe, private readonly teamPipe: TeamPipe) { }


	getQuery(id: string): Promise<QueryModel> {
		return firstValueFrom(this.http.get<QueryModel>(`${this.baseUrl}/${id}`));
	}

	getQueries(): Promise<QueryModel[]> {
		return firstValueFrom(this.http.get<QueryModel[]>(`${this.baseUrl}`));
	}

	saveQuery(query: QueryModel, queryData: DynamicQueryModel): Promise<QueryModel> {
		const request = new SaveQueryRequestModel();
		request.query = query;
		request.queryData = queryData;
		request.include(r => r.query);
		request.include(r => r.queryData);
		request.queryData.include(qd => qd.filters);
		request.queryData.include(qd => qd.selectProperties);
		request.queryData.filters.forEach(filter => filter.include(f => f.property));
		return firstValueFrom(this.http.post<QueryModel>(`${this.baseUrl}`, request));
	}

	deleteQuery(queryId: string): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${queryId}`));
	}

	private queryProperties?: Promise<Record<string, QueryPropertyModel[]>>;
	// Query properties are static definitions, so all query types are fetched once and cached.
	async getQueryProperties(queryType: QueryType): Promise<QueryPropertyModel[]> {
		if (!this.queryProperties) {
			this.queryProperties = firstValueFrom(this.http.get<Record<string, QueryPropertyModel[]>>(`${this.baseUrl}/get-query-properties`));
			this.queryProperties.catch(() => this.queryProperties = undefined);
		}
		const properties = (await this.queryProperties)[QueryType[queryType]] ?? [];
		return properties.map(property => RelationService.instantiateModelsFromData(new QueryPropertyModel(), { ...property }));
	}

	// Display formats a select property can switch between, grouped by the kind of value they display.
	private static readonly formatTypeGroups: { type: string, languageKey: string; }[][] = [
		[{ type: 'boolean', languageKey: 'boolean' }],
		[{ type: 'number', languageKey: 'number' }, { type: 'currency', languageKey: 'currency' }],
		[{ type: 'Date', languageKey: 'date' }, { type: 'shortDate', languageKey: 'shortDate' }],
		[{ type: 'guid', languageKey: 'guid' }, { type: 'user', languageKey: 'user' }, { type: 'team', languageKey: 'team' }],
	];

	getAvailableFormatTypes(property: SelectPropertyModel) {
		return QueryService.formatTypeGroups.find(group => group.some(format => format.type == property.type)) ?? [{ type: 'string', languageKey: 'string' }];
	}

	/** Replaces each filter's property, which may only contain the name (e.g. from the AI query builder), with the full definition. */
	resolveFilterProperties(filters: ExpressionFilterModel[], queryProperties: QueryPropertyModel[]) {
		filters.forEach(filter => {
			const property = queryProperties.find(p => p.name.toLowerCase() == filter.property?.name?.toLowerCase());
			// Each filter gets its own copy, since requests are decycled and a shared object would be sent as a $ref that the server can't resolve
			if (property) filter.property = RelationService.instantiateModelsFromData(new QueryPropertyModel(), RelationService.deepClone(property));
			// Select values are saved as numbers (the enum value); older queries and the select list use the string key
			if (property?.type == QueryPropertyType.Select) filter.value = QueryService.toSelectValue(filter.value);
		});
	}

	/** Copies a query's data and resolves its filters' property definitions, so it can be edited without changing the original. */
	async loadQueryData(data: DynamicQueryModel | InlineQuery): Promise<{ queryData: DynamicQueryModel, queryProperties: QueryPropertyModel[] }> {
		const queryData = QueryService.cloneQueryData(data);
		const queryProperties = await this.getQueryProperties(queryData.queryType);
		this.resolveFilterProperties(queryData.filters, queryProperties);
		return { queryData, queryProperties };
	}

	static cloneQueryData(data: DynamicQueryModel | InlineQuery): DynamicQueryModel {
		return RelationService.instantiateModelsFromData(new DynamicQueryModel(), RelationService.deepClone(data));
	}

	/** Builds an inline sub-query from filters, with each filter's value given by getValue. Filters without a property are left out. */
	static toInlineQuery<TFilter extends ExpressionFilterModel>(queryType: QueryType, filters: TFilter[], getValue: (filter: TFilter, index: number) => FilterValue): InlineQuery {
		return {
			queryType,
			filters: filters.flatMap((filter, index) => filter.property
				? [{ property: { name: filter.property.name }, operator: filter.operator, value: getValue(filter, index) }]
				: []),
			selectProperties: [],
		};
	}

	/** The operators a filter on the property can use. */
	static getComparisons(property: QueryPropertyModel): Comparison[] {
		const comparisons = QueryService.getComparisonsForType(property);
		return property.nullable ? [...comparisons, Comparison.NotNull, Comparison.Null] : comparisons;
	}

	private static getComparisonsForType(property: QueryPropertyModel): Comparison[] {
		if (property.type == QueryPropertyType.Query) return [Comparison.Equal, Comparison.In, Comparison.NotIn];
		if (property.type != QueryPropertyType.Value) return [Comparison.Equal];
		switch (property.valueType) {
			case QueryPropertyValueType.Text:
				return [Comparison.Equal, Comparison.Contains, Comparison.StartsWith, Comparison.EndsWith];
			case QueryPropertyValueType.Numeric:
			case QueryPropertyValueType.Date:
			case QueryPropertyValueType.DateTime:
			case QueryPropertyValueType.Time:
				return [Comparison.Equal, Comparison.NotEqual, Comparison.GreaterThan, Comparison.GreaterThanOrEqual, Comparison.LessThan, Comparison.LessThanOrEqual];
			default:
				return [Comparison.Equal];
		}
	}

	static toSelectValue(value: any): any {
		return typeof value == 'string' && value.trim() != '' && !isNaN(Number(value)) ? Number(value) : value;
	}

	static isQueryReference(value: unknown): value is QueryReference {
		return value != null && typeof value == 'object' && typeof (value as Partial<QueryReference>).queryRef == 'string';
	}

	static isInlineQuery(value: unknown): value is InlineQuery {
		return value != null && typeof value == 'object' && Array.isArray((value as Partial<InlineQuery>).filters);
	}

	buildQuery(prompt: string, history: QueryBuilderMessage[]): Promise<QueryBuilderResponse> {
		return firstValueFrom(this.http.post<QueryBuilderResponse>(`${environment.gatewayUrl}/api/ai/query-builder`, { prompt, history }));
	}

	executeFilterQuery<T>(dynamicQuery: DynamicQueryModel): Promise<T> {
		dynamicQuery.include(query => query.filters);
		dynamicQuery.include(query => query.selectProperties);
		dynamicQuery.filters.forEach(filter => filter.include(f => f.property));
		return firstValueFrom(this.http.post<T>(`${this.baseUrl}/execute-dynamic-query`, dynamicQuery));
	}

	getPropertyDetails(queryType: QueryType, existingSelectProperties: SelectPropertyModel[] = []): ModelPropertyWithDerivedName[] {
		let properties: ModelPropertyWithDerivedName[] = [];
		switch (queryType) {
			case QueryType.TaskAction:
				properties = this.getDerivedNamesFromModel(new TaskActionModel, 1);
				break;
			case QueryType.Taskflow:
				properties = this.getDerivedNamesFromModel(new TaskflowModel, 1);
				break;
			case QueryType.Company:
				properties = this.getDerivedNamesFromModel(new CompanyModel, 1);
				break;
			case QueryType.Order:
				properties = this.getDerivedNamesFromModel(new OrderModel, 1);
				break;
			case QueryType.Product:
				properties = this.getDerivedNamesFromModel(new ProductModel, 1);
				break;
			case QueryType.ProductPlan:
				properties = this.getDerivedNamesFromModel(new ProductPlanModel, 1);
				break;
			case QueryType.OrderCancellation:
				properties = this.getDerivedNamesFromModel(new OrderCancellationModel, 1);
				break;
			case QueryType.OrderWarning:
				properties = this.getDerivedNamesFromModel(new OrderWarningModel, 1);
				break;
			case QueryType.OrderLine:
				properties = this.getDerivedNamesFromModel(new OrderLineModel, 1);
				break;
			case QueryType.OrderProductPlanDiscontinuation:
				properties = this.getDerivedNamesFromModel(new OrderProductPlanDiscontinuationModel, 1);
				break;
			case QueryType.Settlement:
				properties = this.getDerivedNamesFromModel(new SettlementModel, 1);
				break;
			case QueryType.Transfer:
				properties = this.getDerivedNamesFromModel(new TransferModel, 1);
				break;
		}
		const missingProperties = existingSelectProperties.filter(sp => !properties.find(p => p.derivedName == sp.name));
		for (const missingProperty of missingProperties) {
			let propertyNameArray = missingProperty.name.split('.');
			const propertyName = propertyNameArray.pop()?.toLowerCase() ?? missingProperty.name;
			const modelName = propertyNameArray.pop()?.toLowerCase() ?? '';
			properties.push({
				name: missingProperty.name,
				type: missingProperty.type,
				isHidden: missingProperty.isHidden,
				isArray: missingProperty.isArray,
				derivedName: missingProperty.name,
				languageString: this.getPropertyLanguageString(propertyName, modelName)
			});
		}
		return properties;
	}

	getDerivedNamesFromModel<T extends GeneratedModel>(model: T, maxDepth: number, currentDepth: number = 0, prefix: string = ''): ModelPropertyWithDerivedName[] {
		function getModelPropertiesWithRelations<T extends GeneratedModel>(model: T, maxDepth: number, currentDepth: number = 0, prefix: string = '', isArray: boolean | undefined = undefined, traversedObjects: string[] = []): ModelPropertyWithDerivedName[] {
			let returnValue = model.getProperties().map(p => ({ ...p, derivedName: prefix + p.name, isArray: isArray != undefined ? isArray : p.isArray }));

			if (traversedObjects.indexOf(model.constructor.name) != -1)
				return [];
			traversedObjects.push(model.constructor.name);
			if (currentDepth < maxDepth) {
				for (const property of returnValue) {
					let newObj = property.new ? property.new() : null;
					if (newObj) {
						returnValue = returnValue.concat(getModelPropertiesWithRelations(Object.getPrototypeOf(newObj), maxDepth, currentDepth + 1, prefix + property.name + '.', property.isArray, traversedObjects));
					}
				}
			}
			return returnValue;
		}
		let result = getModelPropertiesWithRelations(model, maxDepth, currentDepth, prefix).filter(p => !p.isObject);
		return result;
	}

	getPropertyHeader(queryType: QueryType, property: SelectPropertyModel): string {
		return this.getPropertyLanguageString(property.name, QueryType[queryType!])
	}

	getPropertyValue(result: QueryResults, property: SelectPropertyModel): string {
		function getDeepestPropertyValue(obj: any, prop: string) {
			if (prop.indexOf('.') != -1) {
				return getDeepestPropertyValue(obj[prop.split('.')[0]], prop.slice(prop.indexOf('.') + 1));
			}
			return obj[prop];
		}
		let propertyValue;
		if (property.isArray && property.aggregationType !== undefined) {
			switch (property.aggregationType) {
				case AggregationEnum.Count:
					propertyValue = this.Count(result, property.name);
					break;
				case AggregationEnum.Sum:
					propertyValue = this.Sum(result, property.name);
					break;
				case AggregationEnum.Min:
					propertyValue = this.Min(result, property.name);
					break;
				case AggregationEnum.Max:
					propertyValue = this.Max(result, property.name);
					break;
			}
		}
		else if (!property.isArray) {
			propertyValue = getDeepestPropertyValue(result, property.name);
		}
		if (property.type === 'Date')
			return this.datePipe.transform(propertyValue);
		if (property.type === 'shortDate')
			return this.datePipe.transform(propertyValue, 'shortDate');
		if (property.type === 'currency')
			return this.currencyPipe.transform(propertyValue);
		if (property.type === 'boolean')
			return this.translate.transform('general.' + propertyValue);
		if (property.type === 'user')
			return this.userPipe.transform(propertyValue, 'name');
		if (property.type === 'team')
			propertyValue = this.teamPipe.transform(propertyValue);
		return String(propertyValue);
	}

	private getAggregatedValues(node: any, path: string | string[]): any[] {
		const segments = typeof path === "string" ? path.split(".") : path;

		if (node == null) return [];
		if (Array.isArray(node)) return node.flatMap(n => this.getAggregatedValues(n, segments));
		if (segments.length === 0) return [node];

		const [head, ...rest] = segments;
		return this.getAggregatedValues(node[head], rest);
	};

	private Count(obj: QueryResults, path: string): number {
		return this.getAggregatedValues(obj, path).length;
	}

	private Sum(obj: QueryResults, path: string): number {
		return this.getAggregatedValues(obj, path)
			.filter((v): v is number => typeof v === "number" && !Number.isNaN(v))
			.reduce((acc, v) => acc + v, 0);
	}

	private Min<T>(obj: QueryResults, path: string): T | undefined {
		return this.getAggregatedValues(obj, path).reduce((m, v) => (m === undefined || v < m ? v : m), undefined);
	}

	private Max<T>(obj: QueryResults, path: string): T | undefined {
		return this.getAggregatedValues(obj, path).reduce((m, v) => (m === undefined || v > m ? v : m), undefined);
	}

	setSelectProperty(property: SelectPropertyModel, derivedProperty: ModelPropertyWithDerivedName, isHidden: boolean, aggregationType?: AggregationEnum) {
		property.name = derivedProperty.derivedName;
		property.type = derivedProperty.type ?? 'string';
		property.isHidden = isHidden;
		property.isArray = derivedProperty.isArray ?? false;
		property.aggregationType = property.isArray ? aggregationType : undefined;
	}

	getPropertyLanguageString(propertyName: string, originalModelName: string) {
		const modelName = propertyName.indexOf('.') != -1 ? propertyName.split('.')[propertyName.split('.').length - 2] : originalModelName;
		return modelName.toLowerCase() + '.property.' + propertyName.split('.')[propertyName.split('.').length - 1].toLowerCase();
	}
}

class GeneratedModel {
	getProperties = (): ModelProperty[] => [];
}
interface ModelPropertyWithDerivedName extends ModelProperty {
	derivedName: string;
	isArray: boolean | undefined;
}
